/**
 * 기관 환경설정 — 좌측 모듈 트리 + 우측 항목 정의
 * (동작 연동 여부는 status로 표시. 추후 항목 추가·제거 가능)
 *
 * status:
 *   - linked : 이미 업무로직에 반영
 *   - stored : 이 화면에서 저장만 (연동 예정)
 *   - info   : 안내·마스터 안내 (값 저장 없음 또는 참고)
 */

import {
  FINAL_BUDGET_MODE_OPTIONS,
  PRIOR_YEAR_BASIS_OPTIONS,
} from 'budget/budgetTenantSettingsStore';
import { MASTER_DETAIL_TAB_DEFS } from 'pages/hr/employeeMaster/masterDetailTabConfig';
import { TREE_MODE_OPTIONS, CALC_LEVEL_OPTIONS } from 'budget/budgetExpenseRequestStore';
import { CHANGE_MATCH_MODE_OPTIONS } from 'budget/budgetChangeStore';
import {
  OBLIGATION_CONFIRM_MODE_OPTIONS,
  ACCOUNTING_MODE_OPTIONS,
  ALLOW_NEGATIVE_EXEC_OPTIONS,
} from 'expense/expenseStore';
import {
  EXPENSE_LOAD_TABS_OPTIONS,
  EXPENSE_LOAD_DEFAULT_TAB_OPTIONS,
  UPSTREAM_DUP_MODE_OPTIONS,
} from 'expense/expenseLoadUtils';
import {
  INCOME_ACCOUNTING_MODE_OPTIONS,
  RECEIPT_ON_INCOME_APPROVE_OPTIONS,
} from 'income/incomeStore';

/** @typedef {'select'|'text'|'toggle'|'number'|'multiCheck'|'note'} EnvControlType */
/** @typedef {'linked'|'stored'|'info'} EnvFieldStatus */

/**
 * @typedef {{
 *   id: string,
 *   label: string,
 *   description?: string,
 *   key: string,
 *   control: EnvControlType,
 *   options?: { value: string, label: string }[],
 *   status: EnvFieldStatus,
 *   placeholder?: string,
 *   min?: number,
 *   max?: number,
 * }} EnvFieldDef
 */

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   description: string,
 *   sections: { id: string, title: string, fields: EnvFieldDef[] }[],
 * }} EnvModuleDef
 */

/** 좌측 네비 그룹 */
export const ENV_NAV_GROUPS = [
  {
    id: 'grp-budget',
    label: '예산관리',
    items: [{ id: 'budget', label: '예산 편성·현액' }],
  },
  {
    id: 'grp-expense',
    label: '지출관리',
    items: [{ id: 'expense', label: '품의·원인행위·결의' }],
  },
  {
    id: 'grp-income',
    label: '수입관리',
    items: [{ id: 'income', label: '결의·수납·회계' }],
  },
  {
    id: 'grp-master',
    label: '기준정보',
    items: [
      { id: 'master', label: '조직·마스터' },
      { id: 'hr', label: '인사' },
    ],
  },
  {
    id: 'grp-access',
    label: '접근·표시',
    items: [
      { id: 'appearance', label: '표시·테마·글꼴' },
      { id: 'logo', label: '기관 로고' },
      { id: 'seal', label: '직인' },
      { id: 'access', label: '권한·화면표시' },
    ],
  },
  {
    id: 'grp-form',
    label: '양식',
    items: [
      { id: 'formRegistry', label: '양식 레지스트리' },
      { id: 'formMatch', label: '매칭 규칙' },
      { id: 'formAcl', label: '사용 권한' },
      { id: 'reportCatalog', label: '서식 카탈로그', masterOnly: true },
    ],
  },
];

const AMOUNT_UNIT_OPTIONS = [
  { value: 'thousand', label: '천원 (현재 구현)' },
  { value: 'won', label: '원' },
];

const ROUND_POLICY_OPTIONS = [
  { value: 'expenseCeilIncomeFloor', label: '지출 절상 · 수입 절사 (현재)' },
  { value: 'roundHalfUp', label: '반올림' },
  { value: 'truncate', label: '절사' },
];

const MONTHLY_DIST_OPTIONS = [
  { value: 'month', label: '월' },
  { value: 'quarter', label: '분기' },
  { value: 'half', label: '반기' },
  { value: 'year', label: '연' },
];

const TREE_OPTS = (TREE_MODE_OPTIONS || []).map((o) => ({
  value: String(o.value ?? o.code ?? ''),
  label: String(o.label ?? o.name ?? o.value ?? ''),
}));

const CALC_MAX_OPTS = (CALC_LEVEL_OPTIONS || []).map((o) => ({
  value: String(o.value ?? o),
  label: `${o.label ?? o.value ?? o}레벨`,
}));

const HR_TAB_OPTS = MASTER_DETAIL_TAB_DEFS.map((t) => ({
  value: t.id,
  label: t.label,
}));

/** @type {Record<string, EnvModuleDef>} */
export const ENV_MODULES = {
  budget: {
    id: 'budget',
    title: '예산 편성·현액',
    description: '최종예산 산정·전년 비교·성립전/간주 표시명과 편성 화면 기본값을 설정합니다.',
    sections: [
      {
        id: 'final',
        title: '최종·전년 기준',
        fields: [
          {
            id: 'finalBudgetMode',
            key: 'finalBudgetMode',
            label: '최종예산 모드',
            description:
              'sumDelta: 확정 차수 증감 합=최종 · lastRound: 마지막 확정 절대액=최종',
            control: 'select',
            options: FINAL_BUDGET_MODE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            })),
            status: 'linked',
          },
          {
            id: 'priorYearBasis',
            key: 'priorYearBasis',
            label: '전년도 비교 기준',
            description: '본예산 기정 시 전년액 — 전년 본예산만 / 전년 최종',
            control: 'select',
            options: PRIOR_YEAR_BASIS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            })),
            status: 'linked',
          },
        ],
      },
      {
        id: 'spare',
        title: '성립전·간주',
        fields: [
          {
            id: 'spare1Label',
            key: 'spare1Label',
            label: '성립전 표시명',
            description: '예산차수·화면에서 쓰는 spare1 라벨 (저장됨 · 라벨 바인딩은 점진 적용)',
            control: 'text',
            status: 'stored',
            placeholder: '성립전',
          },
          {
            id: 'spare2Label',
            key: 'spare2Label',
            label: '간주예산 표시명',
            description: 'spare2 라벨 (저장됨 · 라벨 바인딩은 점진 적용)',
            control: 'text',
            status: 'stored',
            placeholder: '간주예산',
          },
          {
            id: 'maxPreSeq',
            key: 'maxPreSeq',
            label: '성립전 최대 횟수',
            description: '추경 차수당 성립전 상한 (현재 코드 상수 9 · 연동 예정)',
            control: 'number',
            min: 1,
            max: 9,
            status: 'stored',
          },
          {
            id: 'maxDeemedSeq',
            key: 'maxDeemedSeq',
            label: '간주 최대 횟수',
            description: '간주 슬롯 상한 (현재 코드 상수 100 · 연동 예정)',
            control: 'number',
            min: 1,
            max: 100,
            status: 'stored',
          },
        ],
      },
      {
        id: 'formation',
        title: '편성·산출',
        fields: [
          {
            id: 'amountUnit',
            key: 'amountUnit',
            label: '금액 단위',
            description: '편성·현액 표시/저장 단위 (현재 천원 고정 · 연동 예정)',
            control: 'select',
            options: AMOUNT_UNIT_OPTIONS,
            status: 'stored',
          },
          {
            id: 'roundPolicy',
            key: 'roundPolicy',
            label: '원→천원 환산',
            description: '지출 절상·수입 절사 등 (calcFormula · 연동 예정)',
            control: 'select',
            options: ROUND_POLICY_OPTIONS,
            status: 'stored',
          },
          {
            id: 'calcLevelMax',
            key: 'calcLevelMax',
            label: '산출레벨 상한',
            description: '산출 디테일 최대 레벨 (현재 1~5 · 연동 예정)',
            control: 'select',
            options: CALC_MAX_OPTS.length
              ? CALC_MAX_OPTS
              : [
                  { value: '3', label: '3레벨' },
                  { value: '5', label: '5레벨' },
                ],
            status: 'stored',
          },
          {
            id: 'defaultTreeMode',
            key: 'defaultTreeMode',
            label: '트리 기본 보기',
            description: '편성요구 조회 시 품목/사업 트리 기본값 (연동 예정)',
            control: 'select',
            options: TREE_OPTS.length
              ? TREE_OPTS
              : [
                  { value: 'SUBJECT', label: '품목' },
                  { value: 'PROJECT', label: '사업' },
                ],
            status: 'stored',
          },
          {
            id: 'monthlyBudgetLabel',
            key: 'monthlyBudgetLabel',
            label: '월별예산 표시명',
            description: '버튼·다이얼로그 타이틀 (연동 예정)',
            control: 'text',
            status: 'stored',
            placeholder: '월별예산',
          },
          {
            id: 'monthlyDistDefault',
            key: 'monthlyDistDefault',
            label: '월별 배분 기본',
            description: '월별예산 일괄 배분 기본 방식 (연동 예정)',
            control: 'select',
            options: MONTHLY_DIST_OPTIONS,
            status: 'stored',
          },
        ],
      },
      {
        id: 'change',
        title: '예산변경',
        fields: [
          {
            id: 'changeMatchMode',
            key: 'changeMatchMode',
            label: '변경 매칭 방식',
            description:
              'ONE_TO_ONE: 변경전→변경후 1행 · N_TO_N: 축별 ±증감(합계 0)',
            control: 'select',
            options: CHANGE_MATCH_MODE_OPTIONS,
            status: 'stored',
          },
        ],
      },
      {
        id: 'note-budget',
        title: '참고',
        fields: [
          {
            id: 'adjStagesNote',
            key: '_noteAdjStages',
            label: '기본 조정단계 템플릿',
            description:
              '부서요구·1차·2차조정 등은 예산차수등록·전년이관에서 관리합니다. 환경설정 이관은 추후 검토.',
            control: 'note',
            status: 'info',
          },
          {
            id: 'carryNote',
            key: '_noteCarry',
            label: '전년 예산이관',
            description:
              '모드별 이관 규칙(sumDelta→본만 / lastRound→선택 차수)은 설계 문서 후속 · FE 미완.',
            control: 'note',
            status: 'info',
          },
        ],
      },
    ],
  },
  expense: {
    id: 'expense',
    title: '지출관리',
    description: '지출품의·원인행위·결의·지급 PoC 옵션. 예산한도·CMS·전표 실연동은 테스트 단계.',
    sections: [
      {
        id: 'obligation',
        title: '원인행위',
        fields: [
          {
            id: 'obligationConfirmMode',
            key: 'obligationConfirmMode',
            label: '원인행위 확정 방식',
            description:
              'CONFIRM: 등록화면에서 확정 · APPROVE: 승인요청 후 승인화면에서 승인(GW연동 대비)',
            control: 'select',
            options: OBLIGATION_CONFIRM_MODE_OPTIONS,
            status: 'linked',
          },
        ],
      },
      {
        id: 'load',
        title: '불러오기·이중생성',
        fields: [
          {
            id: 'resolutionLoadTabs',
            key: 'resolutionLoadTabs',
            label: '결의 불러오기 탭',
            description: '원인행위·현액 탭 표시. BOTH면 한 팝업에 탭으로 제공',
            control: 'select',
            options: EXPENSE_LOAD_TABS_OPTIONS,
            status: 'linked',
          },
          {
            id: 'resolutionLoadDefaultTab',
            key: 'resolutionLoadDefaultTab',
            label: '결의 기본 탭',
            description: '불러오기 팝업 열 때 우선 탭 (UPSTREAM=원인행위)',
            control: 'select',
            options: EXPENSE_LOAD_DEFAULT_TAB_OPTIONS,
            status: 'linked',
          },
          {
            id: 'obligationLoadTabs',
            key: 'obligationLoadTabs',
            label: '원인행위 불러오기 탭',
            description: '품의·현액 탭 표시',
            control: 'select',
            options: EXPENSE_LOAD_TABS_OPTIONS,
            status: 'linked',
          },
          {
            id: 'obligationLoadDefaultTab',
            key: 'obligationLoadDefaultTab',
            label: '원인행위 기본 탭',
            description: '불러오기 팝업 열 때 우선 탭 (UPSTREAM=품의)',
            control: 'select',
            options: EXPENSE_LOAD_DEFAULT_TAB_OPTIONS,
            status: 'linked',
          },
          {
            id: 'upstreamDupMode',
            key: 'upstreamDupMode',
            label: '현액 직등록 시 앞단 존재',
            description:
              '동일 예산축에 품의/원인행위가 있을 때 WARN 확인 · BLOCK 차단 · OFF 무시',
            control: 'select',
            options: UPSTREAM_DUP_MODE_OPTIONS,
            status: 'linked',
          },
        ],
      },
      {
        id: 'resolution',
        title: '지출결의·회계',
        fields: [
          {
            id: 'accountingMode',
            key: 'accountingMode',
            label: '회계 처리 방식',
            description:
              'MATCHED: 결의승인 시 전표생성 · SEPARATED: 분개검증 후 전표(PoC는 플래그만)',
            control: 'select',
            options: ACCOUNTING_MODE_OPTIONS,
            status: 'stored',
          },
          {
            id: 'allowNegativeExec',
            key: 'allowNegativeExec',
            label: '마이너스 집행',
            description: '특정 조건에서 마이너스 집행 허용 (검증 로직 후속)',
            control: 'select',
            options: ALLOW_NEGATIVE_EXEC_OPTIONS,
            status: 'stored',
          },
        ],
      },
    ],
  },
  income: {
    id: 'income',
    title: '수입관리',
    description: '수입결의·수납·조정·과오납·불납 PoC. 승인 시 전표 stub · 실전표는 테스트 단계.',
    sections: [
      {
        id: 'accounting',
        title: '회계·수납',
        fields: [
          {
            id: 'accountingMode',
            key: 'accountingMode',
            label: '회계 처리 방식',
            description:
              'MATCHED: 승인 시 전표 즉시 · SEPARATED: 분개검증 후 전표(PoC는 slipYn 플래그)',
            control: 'select',
            options: INCOME_ACCOUNTING_MODE_OPTIONS,
            status: 'stored',
          },
          {
            id: 'receiptOnIncomeApprove',
            key: 'receiptOnIncomeApprove',
            label: '수입승인 시 수납',
            description:
              'Y: 수입결의 승인 시 수납결의까지 생성(현금주의) · N: 수납 별도(발생주의)',
            control: 'select',
            options: RECEIPT_ON_INCOME_APPROVE_OPTIONS,
            status: 'linked',
          },
        ],
      },
    ],
  },
  master: {
    id: 'master',
    title: '조직·마스터',
    description: '기관·부서·회계 축 등 기준정보. 대부분 마스터 화면에서 등록하며, 여기에는 정책성 옵션만 둡니다.',
    sections: [
      {
        id: 'policy',
        title: '검색·입력 정책',
        fields: [
          {
            id: 'lockSearchDeptForNonMaster',
            key: 'lockSearchDeptForNonMaster',
            label: '비마스터 부서 고정',
            description:
              '켜면 일반 사용자는 편성요구/조정 검색 부서를 소속으로 고정 (현재 isMaster 하드코딩 · 연동 예정)',
            control: 'toggle',
            status: 'stored',
          },
        ],
      },
      {
        id: 'master-note',
        title: '마스터 화면에서 관리',
        fields: [
          {
            id: 'tenantNote',
            key: '_noteTenant',
            label: '기관 사용여부·회계기수',
            description: '기관등록 화면에서 관리합니다.',
            control: 'note',
            status: 'info',
          },
          {
            id: 'acctNote',
            key: '_noteAcct',
            label: '회계구분·회계단위',
            description: '회계구분등록·회계단위등록 마스터에서 관리합니다.',
            control: 'note',
            status: 'info',
          },
          {
            id: 'deptRoleNote',
            key: '_noteDeptRole',
            label: '부서 예산역할',
            description: '부서관리의 편성/집행 역할 속성으로 관리합니다.',
            control: 'note',
            status: 'info',
          },
        ],
      },
    ],
  },
  hr: {
    id: 'hr',
    title: '인사',
    description: '인사마스터 등 인사 모듈 표시·동작 옵션입니다.',
    sections: [
      {
        id: 'hr-tabs',
        title: '인사마스터',
        fields: [
          {
            id: 'hiddenMasterTabs',
            key: 'hiddenMasterTabs',
            label: '숨길 상세 탭',
            description: '체크한 탭은 인사마스터 우측 상세에서 숨깁니다. (localStorage · 연동됨)',
            control: 'multiCheck',
            options: HR_TAB_OPTS,
            status: 'linked',
          },
        ],
      },
      {
        id: 'hr-note',
        title: '참고',
        fields: [
          {
            id: 'paramNote',
            key: '_noteParam',
            label: '규정 파라미터',
            description: '규정 파라미터 화면은 플레이스홀더입니다. 본문 연동 후 이관 검토.',
            control: 'note',
            status: 'info',
          },
        ],
      },
    ],
  },
  appearance: {
    id: 'appearance',
    title: '표시·테마·글꼴',
    description:
      '기관 메인 색·라이트/다크와 함께, 무료 상용 글꼴과 권장 글자 크기를 정합니다. 글자는 rem으로만 커지고 Ctrl+휠처럼 화면 전체가 줄지 않습니다.',
    sections: [
      {
        id: 'appearance-panel',
        title: '브랜드·모드·글꼴',
        fields: [
          {
            id: 'appearanceEditor',
            key: '_appearanceEditor',
            label: '테마 편집',
            description: '전용 패널에서 편집 · appearanceTenant 저장',
            control: 'appearance',
            status: 'linked',
          },
        ],
      },
    ],
  },
  logo: {
    id: 'logo',
    title: '기관 로고',
    description:
      '로그인한 기관의 사이드바 상단 CI를 등록합니다. PNG(투명 배경 권장) · JPG · WebP · SVG.',
    sections: [
      {
        id: 'logo-panel',
        title: '로고 등록',
        fields: [
          {
            id: 'tenantLogoEditor',
            key: '_tenantLogo',
            label: '기관 로고',
            description: '로그인 기관코드 기준으로 저장 · erp.tenantLogos',
            control: 'note',
            status: 'linked',
          },
        ],
      },
    ],
  },
  seal: {
    id: 'seal',
    title: '직인',
    description:
      '로그인 기관의 법인 직인과 회계단위(사업장) 직인을 등록합니다. 기관등록·회계단위등록 화면에서는 관리하지 않습니다.',
    sections: [
      {
        id: 'seal-panel',
        title: '직인 등록',
        fields: [
          {
            id: 'sealEditor',
            key: '_seal',
            label: '기관·회계단위 직인',
            description: 'erp.seals|{tenantCode} 저장',
            control: 'note',
            status: 'linked',
          },
        ],
      },
    ],
  },
  access: {
    id: 'access',
    title: '권한·화면표시',
    description: '메뉴·권한은 권한관리 화면, 개인 그리드 선호는 사용자 prefs로 유지됩니다.',
    sections: [
      {
        id: 'access-note',
        title: '안내',
        fields: [
          {
            id: 'menuNote',
            key: '_noteMenu',
            label: 'masterOnly / adminOnly 메뉴',
            description: '메뉴 설정(hrMenuConfig)과 권한관리 화면에서 제어합니다.',
            control: 'note',
            status: 'info',
          },
          {
            id: 'prefsNote',
            key: '_notePrefs',
            label: '그리드·조회접힘 선호',
            description: '사용자·기관 키(prefsUserId)로 화면별 유지 — 업무규칙 옵션이 아닙니다.',
            control: 'note',
            status: 'info',
          },
          {
            id: 'execScopeNote',
            key: '_noteExec',
            label: '집행권한 부서매핑',
            description: '설계 계약만 존재 · FE 미구현.',
            control: 'note',
            status: 'info',
          },
        ],
      },
    ],
  },
  formRegistry: {
    id: 'formRegistry',
    title: '양식 레지스트리',
    description:
      '업무별 HWP 양식 카탈로그 · 업로드 · 기관 활성 연결. (CM-DESIGN-005)',
    sections: [],
  },
  formMatch: {
    id: 'formMatch',
    title: '매칭 규칙',
    description:
      '기관·부서·사업·재원·회계연도 longest-match로 formId 선택. (CM-DESIGN-005 P2)',
    sections: [],
  },
  formAcl: {
    id: 'formAcl',
    title: '사용 권한',
    description:
      '양식별 사용 가능 부서·담당(로그인ID). ACL 미등록 시 전원 허용. (CM-DESIGN-005 P2)',
    sections: [],
  },
  reportCatalog: {
    id: 'reportCatalog',
    title: '서식 카탈로그',
    description:
      '표 리포트 완성 서식 불러오기 · 데이터셋 매핑 · 기관 서식 저장. 조회 화면 출력(개인 문서설정)과 다름. (CM-DESIGN-006)',
    sections: [],
  },
};

export const ENV_STATUS_META = {
  linked: { label: '연동', title: '저장 시 업무 로직에 반영됩니다' },
  stored: { label: '저장만', title: '값은 저장되나 업무 연동은 추후 적용' },
  info: { label: '안내', title: '참고 항목 · 이 화면에서 값을 바꾸지 않습니다' },
};

export const DEFAULT_MODULE_ID = 'budget';
